import { useState, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";

import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { createSingleNotificationCertificateStageOne } from "../../../services/NotificationCertificateService";
import NotificationCertificateSinglePreviewModal from "../../NotificationCertificate/NotificationCertificateSinglePreviewModal";
import {
  NOTIFICATION_STAGE_LABELS,
  notificationStatusChipSx,
  isLegalFileLocked,
} from "./detailHelpers";

import GlassCard from "./GlassCard";
import SectionTitle from "./SectionTitle";
import InfoRow from "./InfoRow";

const NOTIFICATION_COLOR_LABELS = {
  yellow: "AMARILLO",
  orange: "NARANJA",
};

const notificationColorChipSx = (color) => {
  if (color === "yellow") {
    return {
      height: 22,
      borderRadius: 999,
      bgcolor: "#fef9c3",
      color: "#9a6700",
      border: "1px solid #eab308",
      fontWeight: 900,
      "& .MuiChip-label": { px: 1, fontSize: 12 },
    };
  }

  if (color === "orange") {
    return {
      height: 22,
      borderRadius: 999,
      bgcolor: "#ffedd5",
      color: "#c2410c",
      border: "1px solid #fb923c",
      fontWeight: 900,
      "& .MuiChip-label": { px: 1, fontSize: 12 },
    };
  }

  return {
    height: 22,
    borderRadius: 999,
    bgcolor: "rgba(0,0,0,0.06)",
    color: "text.primary",
    fontWeight: 900,
    "& .MuiChip-label": { px: 1, fontSize: 12 },
  };
};

export default function TabCedulas({ notificationCertificates, legalFile, onRefresh, onPreviewCertificate }) {
  const { showToast } = useToast();
  const { hasPermission } = useAuth();

  const [openGenerateNotificationDialog, setOpenGenerateNotificationDialog] = useState(false);
  const [generateNotificationLoading, setGenerateNotificationLoading] = useState(false);
  const [generateNotificationError, setGenerateNotificationError] = useState("");

  const hasStageOneNotification = useMemo(() => {
    return (legalFile?.notification_certificates || []).some(
      (item) => item.stage === "stage_1",
    );
  }, [legalFile]);

  const locked = isLegalFileLocked(legalFile);

  const canGenerateStageOneNotification =
    hasPermission("generar cedula notificacion") &&
    !hasStageOneNotification &&
    !locked;

  const mainDefendant = useMemo(() => {
    return (legalFile?.parties || []).find(
      (party) =>
        String(party.role || "")
          .trim()
          .toUpperCase() === "DEMANDADO PRINCIPAL",
    );
  }, [legalFile]);

  const operationOptions = useMemo(() => {
    return (legalFile?.operations || [])
      .slice()
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [legalFile]);

  const handleOpenGenerateNotificationDialog = () => {
    setOpenGenerateNotificationDialog(true);
  };

  const handleCloseGenerateNotificationDialog = () => {
    if (generateNotificationLoading) return;
    setOpenGenerateNotificationDialog(false);
    setGenerateNotificationError("");
  };

  const handleGenerateSingleNotification = async (data) => {
    if (!data?.operation) {
      setGenerateNotificationError("Debe seleccionar una operación principal.");
      return;
    }

    if (!data?.party) {
      setGenerateNotificationError("No se encontró el demandado principal.");
      return;
    }

    if (!data?.selectedAddress?.address) {
      setGenerateNotificationError("Debe seleccionar o cargar un domicilio.");
      return;
    }

    try {
      setGenerateNotificationLoading(true);
      setGenerateNotificationError("");

      const payload = {
        operation_code: data.operation.number_operation,
        secondary_operation_code:
          data.secondaryOperation?.number_operation || null,
        template_html: data.templateHtml || "",
        party_id: data.party.id,
        selected_address: {
          type: data.selectedAddress.type || "",
          address: data.selectedAddress.address || "",
          city: data.selectedAddress.city || "",
          from_date: data.selectedAddress.from_date || null,
          is_manual: Boolean(data.selectedAddress.is_manual),
        },
      };

      const res = await createSingleNotificationCertificateStageOne(
        legalFile.id,
        payload,
      );

      if (res.data?.status === "success") {
        showToast(res.data.message || "Cédula generada correctamente.", "success");
        setOpenGenerateNotificationDialog(false);
        await onRefresh();
      }
    } catch (error) {
      setGenerateNotificationError(
        error?.response?.data?.message ||
          "No se pudo generar la cédula de notificación.",
      );
    } finally {
      setGenerateNotificationLoading(false);
    }
  };

  return (
    <>
      <GlassCard sx={{ mb: 1.2 }}>
        <SectionTitle
          icon={<AssignmentIcon />}
          title="Cédulas de notificación"
          description="Generación y visualización de cédulas del expediente."
          right={
            canGenerateStageOneNotification ? (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenGenerateNotificationDialog}
                sx={{
                  borderRadius: 2.2,
                  fontWeight: 900,
                  textTransform: "none",
                }}
              >
                Generar cédula
              </Button>
            ) : null
          }
        />
      </GlassCard>
      {!notificationCertificates.length ? (
        <GlassCard>
          <Typography sx={{ fontWeight: 950 }}>
            No hay cédulas de notificación
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.7 }}>
            Cuando el expediente tenga cédulas cargadas, van a
            aparecer acá.
          </Typography>
        </GlassCard>
      ) : (
        <Box
          sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}
        >
          {notificationCertificates.map((item) => {
            const createdDate = item.created_at
              ? new Date(item.created_at).toLocaleDateString("es-AR")
              : "—";

            const notificationDate = item.notification_date
              ? new Date(item.notification_date).toLocaleDateString(
                  "es-AR",
                )
              : "No registrada";

            return (
              <GlassCard
                key={item.id}
                sx={{ p: { xs: 1.4, sm: 1.7 } }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.2,
                    alignItems: "flex-start",
                  }}
                >
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: 3,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(0,0,0,0.04)",
                      flexShrink: 0,
                    }}
                  >
                    <AssignmentIcon />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 950 }} noWrap>
                      {item.code ||
                        item.file_name ||
                        "Cédula de notificación"}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.75 }}
                      noWrap
                    >
                      {NOTIFICATION_STAGE_LABELS[item.stage] ||
                        item.stage ||
                        "—"}{" "}
                      · {createdDate}
                    </Typography>

                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        size="small"
                        label={item.status_text || "—"}
                        sx={notificationStatusChipSx(
                          item.status_text,
                        )}
                      />

                      <Chip
                        size="small"
                        label={item.result_status_text || "—"}
                        sx={notificationStatusChipSx(
                          item.result_status_text,
                        )}
                      />

                      {item.label_color ? (
                        <Chip
                          size="small"
                          label={
                            NOTIFICATION_COLOR_LABELS[
                              item.label_color
                            ] || item.label_color
                          }
                          sx={notificationColorChipSx(
                            item.label_color,
                          )}
                        />
                      ) : null}

                      {item.label_code ? (
                        <Chip
                          size="small"
                          label={`ETIQUETA: ${item.label_code}`}
                          sx={{
                            height: 22,
                            borderRadius: 999,
                            bgcolor: "rgba(0,0,0,0.06)",
                            fontWeight: 900,
                            "& .MuiChip-label": {
                              px: 1,
                              fontSize: 12,
                            },
                          }}
                        />
                      ) : null}
                    </Box>

                    <Divider sx={{ my: 1.2, opacity: 0.55 }} />

                    <Grid container spacing={1.2}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <InfoRow
                          label="Código interno"
                          value={item.code}
                        />
                        <InfoRow
                          label="Código expediente"
                          value={item.extracted_legal_file_code}
                        />
                        <InfoRow
                          label="Fecha notificación"
                          value={notificationDate}
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <InfoRow
                          label="Código etiqueta"
                          value={item.label_code}
                        />
                        <InfoRow
                          label="Código barra inferior"
                          value={item.bottom_barcode_code}
                        />
                        <InfoRow
                          label="Archivo original"
                          value={item.original_name}
                        />
                      </Grid>
                    </Grid>
                  </Box>

                  <Tooltip title="Ver archivo">
                    <span>
                      <IconButton
                        onClick={() => onPreviewCertificate(item.id)}
                        disabled={
                          !hasPermission(
                            "descargar cedulas notificacion",
                          )
                        }
                        sx={{
                          borderRadius: 2,
                          bgcolor: "rgba(0,0,0,0.04)",
                          "&:hover": { bgcolor: "rgba(0,0,0,0.07)" },
                          flexShrink: 0,
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </GlassCard>
            );
          })}
        </Box>
      )}

      <NotificationCertificateSinglePreviewModal
        open={openGenerateNotificationDialog}
        onClose={handleCloseGenerateNotificationDialog}
        onConfirm={handleGenerateSingleNotification}
        loading={generateNotificationLoading}
        legalFile={legalFile}
        mainDefendant={mainDefendant}
        operationOptions={operationOptions}
        apiError={generateNotificationError}
        onClearApiError={() => setGenerateNotificationError("")}
      />
    </>
  );
}
